import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [meRes, itemsRes] = await Promise.all([
          axios.get(`${API}/auth/me`, { withCredentials: true }),
          axios.get(`${API}/admin/submissions`, { withCredentials: true }),
        ]);
        setMe(meRes.data);
        setItems(itemsRes.data);
      } catch {
        nav("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
  }, [nav]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    nav("/admin/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-[11px] uppercase tracking-[0.24em] text-stone-muted">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="admin-dashboard">
      <header className="border-b border-charcoal/20 px-8 py-5 flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-muted">Bosecrafts · Admin</p>
          <h1 className="font-display text-3xl tracking-tightest">Submissions</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-serif text-stone-muted">{me?.email}</span>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="border border-charcoal px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-charcoal hover:text-ivory transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      <div className="grid grid-cols-12 min-h-[calc(100vh-88px)]">
        <div className="col-span-12 md:col-span-5 border-r border-charcoal/20 overflow-auto">
          {items.length === 0 && (
            <p className="p-8 text-sm text-stone-muted" data-testid="admin-empty">
              No submissions yet.
            </p>
          )}
          {items.map((it) => (
            <button
              key={it._id}
              onClick={() => setSelected(it)}
              data-testid={`admin-item-${it._id}`}
              className={`w-full text-left border-b border-charcoal/15 px-6 py-5 hover:bg-cream transition-colors ${
                selected?._id === it._id ? "bg-cream" : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted">
                  {it.kind}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-stone-muted">
                  {new Date(it.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="mt-2 font-serif text-xl">{it.name}</p>
              <p className="text-sm text-stone-muted">{it.email}</p>
            </button>
          ))}
        </div>

        <div className="col-span-12 md:col-span-7 p-8 md:p-12">
          {selected ? (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-stone-muted">
                {selected.kind} · {new Date(selected.created_at).toLocaleString()}
              </p>
              <h2 className="mt-4 font-display text-5xl tracking-tightest">{selected.name}</h2>
              <p className="mt-2 font-serif text-lg">{selected.email}</p>

              <div className="mt-10 grid grid-cols-2 gap-6 font-mono text-[11px] uppercase tracking-[0.22em] text-stone-muted">
                {selected.company && <Line label="Company" value={selected.company} />}
                {selected.project_type && <Line label="Project" value={selected.project_type} />}
                {selected.budget && <Line label="Budget" value={selected.budget} />}
                {selected.timeline && <Line label="Timeline" value={selected.timeline} />}
              </div>

              <div className="mt-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-stone-muted mb-3">
                  {selected.brief ? "Brief" : "Message"}
                </p>
                <p className="font-serif text-lg leading-relaxed whitespace-pre-wrap">
                  {selected.brief || selected.message}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-stone-muted font-mono text-[11px] uppercase tracking-[0.24em]">
              Select a submission to view.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Line({ label, value }) {
  return (
    <div>
      <p className="text-stone-muted/80 mb-1">{label}</p>
      <p className="text-charcoal font-serif text-base normal-case tracking-normal">{value}</p>
    </div>
  );
}
